import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
    <div className="hidden lg:flex flex-col justify-between bg-zinc-950 text-white p-10 relative overflow-hidden">
      <div className="absolute inset-0 zebra-stripes opacity-40" />
      <div className="relative"><Logo className="[&>span:last-child]:text-white" /></div>
      <div className="relative"><h2 className="text-3xl font-semibold leading-tight">Crie, analise e otimize campanhas com inteligência artificial.</h2><p className="text-zinc-400 mt-3">Dados reais. Sugestões claras. Você no controle.</p>
        <ul className="mt-6 space-y-2 text-sm text-zinc-300">{["Conexão oficial com a API do Google Ads", "Nada é alterado sem a sua aprovação", "Pagamento por Pix ou cartão via Mercado Pago"].map((t) => <li key={t} className="flex gap-2"><span className="text-[#34A853]">✓</span>{t}</li>)}</ul></div>
    </div>
    <div className="flex items-center justify-center p-5 sm:p-8">
      <div className="w-full max-w-sm reveal">
        <div className="lg:hidden mb-6"><Logo /></div>
        <h1 className="text-2xl sm:text-3xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(f.email, f.password);
      nav(loc.state?.from || (u.role === "admin" ? "/admin" : "/app"));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Entrar" sub="Acesse sua conta Cem Zebra Ai.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><Label>Email</Label><Input data-testid="login-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11 mt-1.5" /></div>
        <div><div className="flex items-center justify-between"><Label>Senha</Label><Link to="/esqueci-senha" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Esqueci minha senha</Link></div><Input data-testid="login-password-input" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11 mt-1.5" /></div>
        <Button data-testid="login-submit-button" disabled={busy} className="w-full h-11 rounded-full">{busy ? "Entrando..." : "Entrar"}</Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">Ainda não tem conta? <Link to="/cadastro" className="text-foreground underline" data-testid="go-register-link">Criar conta</Link></p>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const plan = qs.get("plan");
  const [f, setF] = useState({ name: "", email: "", password: "", org_name: "", org_type: "individual" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(f);
      if (plan) localStorage.setItem("cz_pending_plan", `${plan}|${qs.get("period") === "annual" ? "annual" : "monthly"}`);
      nav("/app/onboarding");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title="Criar conta" sub="Comece seu teste grátis. Sem cartão agora.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div><Label>Seu nome</Label><Input data-testid="register-name-input" required minLength={2} value={f.name} onChange={set("name")} className="h-11 mt-1.5" /></div>
        <div><Label>Email</Label><Input data-testid="register-email-input" type="email" required value={f.email} onChange={set("email")} className="h-11 mt-1.5" /></div>
        <div><Label>Senha (mín. 8 caracteres)</Label><Input data-testid="register-password-input" type="password" minLength={8} required value={f.password} onChange={set("password")} className="h-11 mt-1.5" /></div>
        <div><Label>Nome da empresa</Label><Input data-testid="register-org-input" value={f.org_name} onChange={set("org_name")} className="h-11 mt-1.5" /></div>
        <div className="grid grid-cols-2 gap-2">
          {[["individual", "Meu negócio"], ["agency", "Sou agência"]].map(([v, l]) => (
            <button type="button" key={v} data-testid={`register-type-${v}`} onClick={() => setF({ ...f, org_type: v })}
              className={`h-11 rounded-xl border text-sm ${f.org_type === v ? "border-primary bg-primary text-primary-foreground" : ""}`}>{l}</button>
          ))}
        </div>
        <Button data-testid="register-submit-button" disabled={busy} className="w-full h-11 rounded-full">{busy ? "Criando..." : "Criar conta"}</Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">Já tem conta? <Link to="/login" className="text-foreground underline">Entrar</Link></p>
    </Shell>
  );
}
